import React, { useState, useEffect, useRef } from "react";
import { GetData, DeleteData, PostData, PutData } from "../../../services/api";
import DataTable from "react-data-table-component";
import { useNavigate } from "react-router-dom";
import { buildExport, downloadJson, exportFilename, readJsonFile, regenerateForImport } from "./workflowTransfer";
import {
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
  Container,
  Row,
  Col,
  Button,
  Badge
} from "reactstrap";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import Loader from "../../../components/Common/Loader";
import Swal from 'sweetalert2';
import moment from "moment";

const Workflows = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const importInputRef = useRef(null);

  // Exportar un flujo a JSON (trae el flujo completo con steps y descarga el archivo)
  const handleExport = async (row) => {
    try {
      const full = await GetData(`/workflows/${row.id}`);
      const wf = full?.data || full;
      downloadJson(buildExport(wf), exportFilename(wf?.name || row.name));
    } catch (err) {
      Swal.fire({ title: "Error", text: err.message || "No se pudo exportar el flujo.", icon: "error" });
    }
  };

  // Importar: leer JSON → regenerar ids/referencias → abrir el builder precargado
  const handleImportClick = () => {
    if (importInputRef.current) importInputRef.current.click();
  };

  const handleImportFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (e.target) e.target.value = ""; // permitir re-importar el mismo archivo
    if (!file) return;
    try {
      const parsed = await readJsonFile(file);
      const imported = regenerateForImport(parsed);
      navigate("/automation/workflows/new", { state: { imported } });
    } catch (err) {
      Swal.fire({ title: "Importación inválida", text: err.message || "No se pudo importar el flujo.", icon: "error" });
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      console.log("Fetching workflows for authenticated user...");
      const response = await GetData("/workflows");
      console.log("Workflows response:", response);
      
      // GetData returns axios response, so data is in response.data
      const workflows = response?.data || response || [];
      const workflowsArray = Array.isArray(workflows) ? workflows : [];
      
      console.log(`Found ${workflowsArray.length} workflows for user`);
      setData(workflowsArray);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching workflows:", err);
      Swal.fire({
        title: "Error",
        text: err.message || "No se pudieron cargar los flujos. Verifica tu conexión y permisos.",
        icon: "error"
      });
      setData([]);
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      active: { color: "success", text: "Activo" },
      paused: { color: "warning", text: "Pausado" },
      draft: { color: "secondary", text: "Borrador" },
    };
    const statusInfo = statusMap[status] || { color: "secondary", text: status };
    return <Badge color={statusInfo.color}>{statusInfo.text}</Badge>;
  };

  const columns = [
    {
      name: <span className="font-weight-bold fs-13">Nombre</span>,
      selector: (row) => row.name,
      sortable: true,
    },
    {
      name: <span className="font-weight-bold fs-13">Trigger</span>,
      selector: (row) => {
        const triggerMap = {
          contact_created: "Contacto Creado",
          contact_updated: "Contacto Actualizado",
          contact_added_to_list: "Agregado a Lista",
          email_opened: "Email Abierto",
          email_clicked: "Email Clickeado",
          scheduled: "Programado",
        };
        // Support both trigger and workflowTrigger for backward compatibility
        const triggerType = row.workflowTrigger?.type || row.trigger?.type;
        return triggerMap[triggerType] || triggerType || "-";
      },
      sortable: true,
    },
    {
      name: <span className="font-weight-bold fs-13">Pasos</span>,
      selector: (row) => row.steps?.length || 0,
      sortable: true,
    },
    {
      name: <span className="font-weight-bold fs-13">Estado</span>,
      cell: (row) => getStatusBadge(row.status),
      sortable: true,
    },
    {
      name: <span className="font-weight-bold fs-13">Creado</span>,
      selector: (row) => moment(row.createdAt).format("DD/MM/YYYY"),
      sortable: true,
    },
    {
      name: <span className="font-weight-bold fs-13">Acciones</span>,
      cell: (row) => (
        <div className="d-flex gap-2 align-items-center">
          <Button
            color="primary"
            size="sm"
            onClick={() => navigate(`/automation/workflows/${row.id}`)}
            title="Editar flujo"
          >
            <i className="ri-edit-line align-middle"></i>
          </Button>
          {row.status === 'active' ? (
            <Button
              color="warning"
              size="sm"
              onClick={() => handlePause(row.id)}
              title="Pausar flujo"
            >
              <i className="ri-pause-line align-middle"></i>
            </Button>
          ) : (
            <Button
              color="success"
              size="sm"
              onClick={() => handleActivate(row.id)}
              title="Activar flujo"
            >
              <i className="ri-play-line align-middle"></i>
            </Button>
          )}
          <UncontrolledDropdown>
            <DropdownToggle className="btn btn-soft-secondary btn-sm" tag="button">
              <i className="ri-more-fill align-middle"></i>
            </DropdownToggle>
            <DropdownMenu>
              <DropdownItem onClick={() => navigate(`/automation/workflows/${row.id}`)}>
                <i className="ri-edit-line align-bottom me-2"></i>Editar
              </DropdownItem>
              <DropdownItem onClick={() => {
                if (row.status === 'active') {
                  handlePause(row.id);
                } else {
                  handleActivate(row.id);
                }
              }}>
                <i className={row.status === 'active' ? 'ri-pause-line' : 'ri-play-line'} align-bottom me-2></i>
                {row.status === 'active' ? 'Pausar' : 'Activar'}
              </DropdownItem>
              <DropdownItem onClick={() => handleExport(row)}>
                <i className="ri-download-2-line align-bottom me-2"></i>Exportar JSON
              </DropdownItem>
              <DropdownItem divider />
              <DropdownItem onClick={() => handleDelete(row.id)} className="text-danger">
                <i className="ri-delete-bin-fill align-bottom me-2"></i>Eliminar
              </DropdownItem>
            </DropdownMenu>
          </UncontrolledDropdown>
        </div>
      ),
    },
  ];

  const handleActivate = async (id) => {
    try {
      await PostData(`/workflows/${id}/activate`);
      Swal.fire({
        title: "Flujo activado",
        text: "El flujo ahora está activo y se ejecutará automáticamente",
        icon: "success",
        timer: 2000,
        showConfirmButton: false
      });
      fetchData();
    } catch (err) {
      Swal.fire({
        title: "Error",
        text: err.message || "No se pudo activar el flujo",
        icon: "error"
      });
    }
  };

  const handlePause = async (id) => {
    Swal.fire({
      title: "¿Pausar flujo?",
      text: "El flujo dejará de ejecutarse hasta que lo actives nuevamente",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Pausar",
      cancelButtonText: "Cancelar",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await PostData(`/workflows/${id}/pause`);
          Swal.fire({
            title: "Flujo pausado",
            text: "El flujo ha sido pausado",
            icon: "success",
            timer: 2000,
            showConfirmButton: false
          });
          fetchData();
        } catch (err) {
          Swal.fire({
            title: "Error",
            text: err.message || "No se pudo pausar el flujo",
            icon: "error"
          });
        }
      }
    });
  };

  const handleDelete = (id) => {
    Swal.fire({
      title: "¿Eliminar flujo?",
      text: "Esta acción no se puede deshacer",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Eliminar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#dc3545",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await DeleteData(`/workflows/${id}`);
          Swal.fire({
            title: "Flujo eliminado",
            text: "El flujo ha sido eliminado correctamente",
            icon: "success",
            timer: 2000,
            showConfirmButton: false
          });
          fetchData();
        } catch (err) {
          Swal.fire({
            title: "Error",
            text: err.message || "No se pudo eliminar el flujo",
            icon: "error"
          });
        }
      }
    });
  };

  return (
    <div className="page-content">
      <Container fluid={true}>
        <Breadcrumbs title="IHubG" breadcrumbItem="Flujos de Automatización" />
        <Row className="my-4">
          <Col className="d-flex gap-2">
            <Button color="success" onClick={() => navigate("/automation/workflows/new")}>
              <i className="ri-add-line align-bottom me-1"></i>Nuevo Flujo
            </Button>
            <Button color="soft-primary" onClick={handleImportClick} title="Importar un flujo desde un archivo JSON">
              <i className="ri-upload-2-line align-bottom me-1"></i>Importar JSON
            </Button>
            <input
              ref={importInputRef}
              type="file"
              accept=".json,application/json"
              style={{ display: "none" }}
              onChange={handleImportFile}
            />
          </Col>
        </Row>
        <Row>
          {loading ? (
            <Loader size={150} />
          ) : (
            <DataTable
              data={data}
              columns={columns}
              noDataComponent={<span className="py-4">Sin flujos. Crea tu primer flujo.</span>}
            />
          )}
        </Row>
      </Container>
    </div>
  );
};

export default Workflows;

